import React, { useEffect, useMemo, useState } from 'react';

import { FileSearchOutlined } from '@ant-design/icons';
import { Button, Input } from 'antd';
import { useTranslation } from 'react-i18next';

import Table from '@components/Table';
import useSearchParams from '@hooks/useSearchParams';
import useSwr from '@hooks/useSwr';
import { dateFormat } from '@utils/formatters';

import RequestReport from '@components/RequestReport';
import rolesConstants from '@utils/rolesConstants';
import ConnectedDevicesPreview from './ConnectedDevicesPreview';

const { Search } = Input;

const ConnectedDevices = () => {
  const { t } = useTranslation('connected-devices');

  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [showDevicesData, setShowDevicesData] = useState();

  const [iccidValue, setIccidValue] = useState<any>();
  const [deviceNumberValue, setDeviceNumberValue] = useState<any>();

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: ['deviceNumber', 'iccid'],
  });

  const { deviceNumber, iccid, page, linesPerPage } = useMemo(
    () => searchParams,
    [searchParams],
  );

  const filters = useMemo(() => {
    const allFilters = {
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage ?? 20,
      deviceNumber,
      iccid,
    };

    return allFilters;
  }, [page, linesPerPage, deviceNumber, iccid]);

  const { data } = useSwr('/service-proxy/transactionTpdu/device', {
    params: filters,
  });

  useEffect(() => {
    setIccidValue(iccid);
  }, [iccid]);
  useEffect(() => {
    setDeviceNumberValue(deviceNumber);
  }, [deviceNumber]);

  const columns = useMemo(() => {
    const allColumns = [
      {
        title: t('table.deviceNumber'),
        align: 'center' as const,
        dataIndex: 'deviceNumber',
      },
      {
        title: t('table.iccid'),
        align: 'center' as const,
        dataIndex: 'iccid',
      },
      {
        title: t('table.imsi'),
        align: 'center' as const,
        dataIndex: 'imsi',
      },
      {
        title: t('table.operator'),
        dataIndex: 'operator',
        align: 'center' as const,
      },
      {
        title: t('table.lastUpdate'),
        dataIndex: 'update',
        align: 'center' as const,
        render: (value: string) => value && dateFormat(value),
      },
      {
        title: t('table.model'),
        dataIndex: 'deviceModel',
        align: 'center' as const,
      },
      {
        title: t('table.details'),
        dataIndex: 'actions',
        align: 'center' as const,
        render: (_: any, deviceOpen: any) => (
          <Button
            style={{ padding: '0 8px' }}
            onClick={() => {
              setShowDevicesData(deviceOpen);
              setShowDevicesModal(true);
            }}
          >
            <FileSearchOutlined />
          </Button>
        ),
      },
    ];

    return allColumns;
  }, [t]);

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('table.iccid'),
        element: (
          <Search
            onSearch={value => setSearchParams({ iccid: value })}
            onChange={({ target: { value } }) => setIccidValue(value)}
            value={iccidValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('table.deviceNumber'),
        element: (
          <Search
            onSearch={value => setSearchParams({ deviceNumber: value })}
            onChange={({ target: { value } }) => setDeviceNumberValue(value)}
            value={deviceNumberValue}
            allowClear
          />
        ),
      },
    ],
    [t, setSearchParams, iccidValue, deviceNumberValue],
  );

  return (
    <div>
      <ConnectedDevicesPreview
        deviceProps={showDevicesData}
        isOpen={showDevicesModal}
        setIsOpen={setShowDevicesModal}
      />
      <Table
        // @ts-ignore
        columns={columns}
        loading={!data?.content}
        title={t('title')}
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        extraActions={
          <RequestReport
            items={[
              {
                role: rolesConstants.REQUEST_REPORT,
                params: filters,
                path: `/service-proxy/transactionTpdu/device/report`,
                type: `TPDU_DEVICE`,
                method: 'get',
              },
            ]}
          />
        }
        onTableChange={setSearchParams}
        pagination={{ page, linesPerPage, total: data?.totalElements }}
        tableKey="connectedDevices"
        data={data?.content || []}
      />
    </div>
  );
};

export default ConnectedDevices;
